import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  getEntityLocationName,
  LOCATION_NAME_REQUIRED_MESSAGE,
} from '@/lib/festival-location';
import { FormButtons, FormRow, LocationMapField, TagsInput } from '../controls';
import { ENTITY_MAP_DEFAULT_ZOOM } from '../controls/interactive-map/constants';
import {
  ModalEntityHelp,
  VENDOR_ADD_HELP_STEPS,
} from '../modal/ModalEntityHelp';

export const ADD_NEW_VENDOR_VALUE = '__new__';

const emptyNewVendor = {
  vendorId: 0,
  vendorName: '',
  vendorContact: '',
  vendorDescription: '',
  category: '',
  subcategory: '',
  links: {
    instagram: '',
    twitter: '',
    facebook: '',
    web: '',
  },
};

function buildDefaultValues(vendor) {
  const selected = vendor.vendor || {};
  const hasExisting = selected.vendorId != null && selected.vendorId !== '';
  return {
    description: vendor.description || '',
    vendor: hasExisting ? selected : {},
    vendorId: hasExisting ? selected.vendorId : '',
  };
}

/**
 * @param {object} props
 * @param {object} props.vendor
 * @param {object[]} props.vendors
 * @param {{ categories?: string[], subcategories?: string[] }} [props.vendorCategories]
 * @param {(vals: object) => void} props.handleSave
 * @param {(location: object, displayName?: string) => void} [props.onLocationChange]
 * @param {(locationName: string) => void} [props.onLocationNameChange]
 * @param {boolean} [props.showHelp]
 * @param {(show: boolean) => void} [props.onShowHelp]
 */
export const FestivalVendorForm = ({
  vendor,
  vendors,
  vendorCategories = {},
  handleSave,
  onLocationChange,
  onLocationNameChange,
  showHelp = false,
  onShowHelp,
}) => {
  const [locationNameError, setLocationNameError] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(vendor),
    mode: 'onSubmit',
  });

  const festivalVendorId = vendor?.vendorId;
  const vendorIdValue = useWatch({ control, name: 'vendorId' });
  const isCreatingNew = vendorIdValue === ADD_NEW_VENDOR_VALUE;

  useEffect(() => {
    reset(buildDefaultValues(vendor));
  }, [festivalVendorId, reset]);

  const categories = vendorCategories.categories || [];
  const subcategories = vendorCategories.subcategories || [];

  const submit = (vals) => {
    if (!getEntityLocationName(vendor)) {
      setLocationNameError(LOCATION_NAME_REQUIRED_MESSAGE);
      return;
    }
    setLocationNameError(null);

    if (vals.vendorId === ADD_NEW_VENDOR_VALUE) {
      const newVendor = vals.vendor || {};
      handleSave({
        description: vals.description,
        vendor: {
          ...newVendor,
          vendorId: 0,
          category: newVendor.category || '',
          subcategory: newVendor.subcategory || '',
        },
        vendorId: 0,
      });
      return;
    }

    const { vendorId: _id, ...rest } = vals;
    handleSave(rest);
  };

  const handleLocationChange = (location, displayName) => {
    if (displayName?.trim()) {
      setLocationNameError(null);
    }
    onLocationChange?.(location, displayName);
  };

  const handleLocationNameChange = (nextLocationName) => {
    if (nextLocationName?.trim()) {
      setLocationNameError(null);
    }
    onLocationNameChange?.(nextLocationName);
  };

  if (showHelp) {
    return (
      <ModalEntityHelp
        entityNoun="vendor"
        steps={VENDOR_ADD_HELP_STEPS}
        showHelp
        onShowHelp={onShowHelp}
        onBack={() => onShowHelp?.(false)}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <ModalEntityHelp
        entityNoun="vendor"
        steps={VENDOR_ADD_HELP_STEPS}
        showHelp={false}
        onShowHelp={() => onShowHelp?.(true)}
        onBack={() => onShowHelp?.(false)}
      />
      <FormRow label="Vendor" error={errors.vendorId?.message}>
        <Controller
          name="vendorId"
          control={control}
          rules={{ required: 'Vendor is required' }}
          render={({ field }) => (
            <select
              className="h-10 w-full rounded-[20px] border border-[var(--input)] px-4 outline-none"
              value={field.value === '' ? '' : String(field.value)}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  field.onChange('');
                  setValue('vendor', {});
                  return;
                }
                if (id === ADD_NEW_VENDOR_VALUE) {
                  field.onChange(ADD_NEW_VENDOR_VALUE);
                  setValue('vendor', { ...emptyNewVendor });
                  return;
                }
                const found = vendors.find(
                  (x) => String(x.vendorId) === String(id),
                );
                field.onChange(found?.vendorId ?? '');
                setValue('vendor', found || {});
              }}
            >
              <option value="">Select a vendor</option>
              {vendors.map((v) => (
                <option key={v.vendorId} value={String(v.vendorId)}>
                  {v.vendorName}
                </option>
              ))}
            </select>
          )}
        />
      </FormRow>

      {isCreatingNew ? (
        <>
          <FormRow
            label="Vendor Name"
            error={errors.vendor?.vendorName?.message}
          >
            <input
              type="text"
              placeholder="Name"
              {...register('vendor.vendorName', {
                required: 'Vendor name is required',
              })}
            />
          </FormRow>
          <FormRow label="Contact">
            <input
              type="text"
              placeholder="Contact"
              {...register('vendor.vendorContact')}
            />
          </FormRow>
          <FormRow label="Category" error={errors.vendor?.category?.message}>
            <Controller
              name="vendor.category"
              control={control}
              rules={{ required: 'Category is required' }}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a category"
                  options={categories}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Subcategory">
            <Controller
              name="vendor.subcategory"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a subcategory"
                  options={subcategories}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Vendor Description">
            <textarea
              placeholder="Vendor Description..."
              {...register('vendor.vendorDescription')}
            />
          </FormRow>
          <FormRow label="Website">
            <input
              type="text"
              placeholder="Website"
              {...register('vendor.links.web')}
            />
          </FormRow>
        </>
      ) : null}

      <FormRow label="Description">
        <textarea
          placeholder="Festival Specific Vendor Description..."
          {...register('description')}
        />
      </FormRow>
      <FormRow error={locationNameError}>
        <LocationMapField
          key={vendor?.vendorId}
          shouldPromptForMap
          defaultZoom={ENTITY_MAP_DEFAULT_ZOOM}
          location={vendor.location}
          locationName={vendor.location?.locationName}
          onLocationChange={handleLocationChange}
          onLocationNameChange={handleLocationNameChange}
        />
      </FormRow>
      <FormButtons title="Vendor" />
    </form>
  );
};
